import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import { useGetDailyPnlQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import dayjs from 'dayjs'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../../features/setting/setting.slice.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import {
  currencyValueFormatter,
  getAxisUnitScale,
} from '../../../../../utils/formatters.ts'
import { useMemo, useRef, useState } from 'react'
import { getAlignedDualAxisScaleConfig } from '../../../../../common/echarts/dual-axis-scale.util.ts'
import { getEchartsTheme } from '../../../../../utils/helpers.ts'
import { useFixedBreakpoint } from '../../../../../common/hooks/use-is-mobile.ts'
import {
  extendPnlData,
  calculatePnlStatistics,
  calculateAxisLabelInterval,
} from './pnl-chart.util'

interface PnlChartProps {
  bookId: string
}

type PnlLegendSelected = {
  PnL: boolean
  'Return on BP': boolean
  'Return on GMV': boolean
  '+1 Std Dev': boolean
  '-1 Std Dev': boolean
  '+2 Std Dev': boolean
  '-2 Std Dev': boolean
}

export const PnlChart: React.FC<PnlChartProps> = ({ bookId }) => {
  const dashboardInput = useAppSelector(selectDashboardInput)
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)
  const echartsTheme = getEchartsTheme(colorTheme)
  const [legendSelected, setLegendSelected] = useState<PnlLegendSelected>({
    PnL: true,
    'Return on BP': false,
    'Return on GMV': false,
    '+1 Std Dev': false,
    '-1 Std Dev': false,
    '+2 Std Dev': false,
    '-2 Std Dev': false,
  })

  const screens = useFixedBreakpoint()

  const { data } = useGetDailyPnlQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  // Keep the latest legend-change logic in a ref so the memoized `onEvents`
  // object below never changes identity. echarts-for-react deep-compares
  // `onEvents` and, when it differs, disposes and recreates the whole chart;
  // an unstable `onEvents` was the cause of the duplicate `legendselectchanged`
  // firing and the right y-axis flash. Calling `setLegendSelected` re-renders
  // the component, so `getAlignedDualAxisScaleConfig` recomputes on every toggle.
  const legendSelectChangedRef = useRef<
    (params: { selected: PnlLegendSelected }) => void
  >(() => {})
  legendSelectChangedRef.current = (params: {
    selected: PnlLegendSelected
  }) => {
    setLegendSelected((prev) => ({
      ...prev,
      ...params.selected,
    }))
  }

  const onEvents = useMemo(
    () => ({
      legendselectchanged: (params: { selected: PnlLegendSelected }) =>
        legendSelectChangedRef.current(params),
    }),
    []
  )

  if (!data) return <></>

  const extendedData = extendPnlData(data.data.personalDailyPnls, 20)
  const stats = calculatePnlStatistics(data.data.personalDailyPnls)
  const labelCount = screens.lg ? 30 : screens.md ? 20 : screens.sm ? 15 : 10
  const interval = calculateAxisLabelInterval(extendedData.length, {
    labelCount,
    interval: 0,
  })

  const {
    primaryAxis: pnlAxisScale,
    secondaryAxis: returnAxisScale,
    secondaryAxisVisible,
  } = getAlignedDualAxisScaleConfig(
    [
      ...extendedData.map((d) => d.pnl),
      stats.plus1Std,
      stats.minus1Std,
      stats.plus2Std,
      stats.minus2Std,
    ],
    [
      {
        values: extendedData.map((d) => d.returnOnBP * 100),
        enabled: legendSelected['Return on BP'],
      },
      {
        values: extendedData.map((d) => d.returnOnGMV * 100),
        enabled: legendSelected['Return on GMV'],
      },
    ]
  )

  const pnlAxisUnit = getAxisUnitScale([pnlAxisScale.min, pnlAxisScale.max])

  const option: EChartsOption = {
    grid: {
      left: 40,
      right: 40,
      bottom: 155,
    },
    xAxis: {
      type: 'category',
      data: extendedData.map((d) => d.date),
      axisLabel: {
        color: themeStyle.sumText,
        rotate: 60,
        interval,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: `PnL ($${pnlAxisUnit.suffix})`,
        // Lock the primary axis to the calculated grid so the secondary axis has a stable structure to match.
        min: pnlAxisScale.min,
        max: pnlAxisScale.max,
        interval: pnlAxisScale.interval,
        splitNumber: pnlAxisScale.splitNumber,
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
          formatter: pnlAxisUnit.labelFormatter,
        },
      },
      {
        type: 'value',
        name: 'Return (%)',
        // Hide the right axis when no secondary series is active to avoid showing an unused scale.
        show: secondaryAxisVisible,
        position: 'right',
        min: returnAxisScale.min,
        max: returnAxisScale.max,
        interval: returnAxisScale.interval,
        splitNumber: returnAxisScale.splitNumber,
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
    ],
    legend: {
      data: [
        'Return on BP',
        'Return on GMV',
        'PnL',
        '+1 Std Dev',
        '-1 Std Dev',
        '+2 Std Dev',
        '-2 Std Dev',
      ],
      selected: legendSelected,
      top: 'bottom',
      textStyle: {
        color: themeStyle.sumText,
      },
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      extraCssText: 'text-align: left;',
      formatter: (
        params: TopLevelFormatterParams | TopLevelFormatterParams[]
      ) => {
        const paramArr = Array.isArray(params) ? params : [params]
        // axisValue is not typed on TopLevelFormatterParams, but is present at runtime for axis tooltips.
        // Use type assertion to access it safely.
        const axisValue =
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValueLabel ??
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValue ??
          (paramArr[0] as { value?: string | number }).value ??
          ''
        let tooltipText = `${axisValue}<br/>`
        paramArr.forEach((param) => {
          const value = (param as { value?: string | number }).value
          const marker = (param as { marker?: string }).marker || ''
          const seriesName = (param as { seriesName?: string }).seriesName || ''
          if (seriesName === 'PnL' || seriesName.includes('Std Dev')) {
            tooltipText += `${marker} ${seriesName}: ${currencyValueFormatter(
              {
                value: value,
              } as ValueFormatterParams,
              { decimal: 0, currency: 'USD' }
            )}<br/>`
          } else if (
            seriesName === 'Return on BP' ||
            seriesName === 'Return on GMV'
          ) {
            tooltipText += `${marker} ${seriesName}: ${Number(
              value
            ).toLocaleString(undefined, { maximumFractionDigits: 2 })}%<br/>`
          } else {
            tooltipText += `${marker} ${seriesName}: ${currencyValueFormatter(
              {
                value: value,
              } as ValueFormatterParams,
              { decimal: 0, currency: 'USD' }
            )}<br/>`
          }
        })
        return tooltipText
      },
    },
    series: [
      {
        type: 'line',
        name: 'Return on BP',
        symbol: 'circle', // show dot for every point
        yAxisIndex: 1,
        data: extendedData.map((d) => d.returnOnBP * 100),
      },
      {
        type: 'line',
        name: 'Return on GMV',
        symbol: 'circle', // show dot for every point
        yAxisIndex: 1,
        data: extendedData.map((d) => Number((d.returnOnGMV * 100).toFixed(2))),
      },
      {
        type: 'bar',
        name: 'PnL',
        data: extendedData.map((d) => d.pnl),
        itemStyle: {
          color: (params) => {
            const value = params.value
            if (typeof value === 'number') {
              return value >= 0 ? '#67b365' : '#f26466'
            }
            return ''
          },
        },
      },
      {
        type: 'line',
        name: '+1 Std Dev',
        symbol: 'circle',
        data: Array(extendedData.length).fill(stats.plus1Std),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '-1 Std Dev',
        symbol: 'circle',
        data: Array(extendedData.length).fill(stats.minus1Std),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '+2 Std Dev',
        symbol: 'circle',
        data: Array(extendedData.length).fill(stats.plus2Std),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '-2 Std Dev',
        symbol: 'circle',
        data: Array(extendedData.length).fill(stats.minus2Std),
        lineStyle: { type: 'dashed' },
      },
    ],
  }

  return (
    <DashboardChartBox title={'PnL chart +/- 1SD, 2SD'}>
      <ReactECharts
        option={option}
        style={{ height: '400px' }}
        theme={echartsTheme.themeName}
        onEvents={onEvents}
      />
    </DashboardChartBox>
  )
}
